/** Lightweight CLD chart primitives (Recharts area/donut + SVG gauge + bars). */
import type { ReactElement, ReactNode } from 'react';
import {
  Area,
  Cell,
  Pie,
  PieChart,
  AreaChart as ReArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface Series {
  name: string;
  color: string;
  data: number[];
}

export function AreaChart({
  labels,
  series,
  height = 260,
}: {
  labels: string[];
  series: Series[];
  height?: number;
}): ReactElement {
  const data = labels.map((label, i) => {
    const row: Record<string, number | string> = { label };
    for (const s of series) row[s.name] = s.data[i] ?? 0;
    return row;
  });
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ReArea data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
        <defs>
          {series.map((s, i) => (
            <linearGradient key={s.name} id={`cld-area-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.2} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0.01} />
            </linearGradient>
          ))}
        </defs>
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: '#98a0ae' }}
          axisLine={false}
          tickLine={false}
          minTickGap={24}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#98a0ae' }}
          axisLine={false}
          tickLine={false}
          width={44}
          tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
        />
        <Tooltip
          contentStyle={{
            borderRadius: 10,
            border: '1px solid #e7e9ee',
            fontSize: 12,
            boxShadow: '0 8px 24px rgba(16,24,40,0.12)',
          }}
        />
        {series.map((s, i) => (
          <Area
            key={s.name}
            type="monotone"
            dataKey={s.name}
            stroke={s.color}
            strokeWidth={2}
            fillOpacity={1}
            fill={`url(#cld-area-${i})`}
          />
        ))}
      </ReArea>
    </ResponsiveContainer>
  );
}

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

export function Donut({
  data,
  size = 168,
  center,
}: {
  data: DonutSlice[];
  size?: number;
  center?: { value: string; label: string };
}): ReactElement {
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <PieChart width={size} height={size}>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          innerRadius={size * 0.32}
          outerRadius={size * 0.48}
          paddingAngle={2}
          stroke="none"
        >
          {data.map((d) => (
            <Cell key={d.label} fill={d.color} />
          ))}
        </Pie>
      </PieChart>
      {center ? (
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div>
            <div className="font-mono text-[19px] font-bold text-text">{center.value}</div>
            <div className="text-[11px] text-text-3">{center.label}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Semi-circular risk gauge (0-100), coloured by band. */
export function Gauge({ value, size = 180 }: { value: number; size?: number }): ReactElement {
  const v = Math.max(0, Math.min(100, value));
  const color = v >= 70 ? '#dc2626' : v >= 40 ? '#d97706' : '#16a34a';
  const r = size * 0.42;
  const cx = size / 2;
  const cy = size * 0.62;
  const a = Math.PI * (1 - v / 100);
  const x = cx + r * Math.cos(a);
  const y = cy - r * Math.sin(a);
  const arc = (frac: number) => {
    const ang = Math.PI * (1 - frac);
    return `${cx + r * Math.cos(ang)},${cy - r * Math.sin(ang)}`;
  };
  return (
    <svg
      width={size}
      height={size * 0.72}
      viewBox={`0 0 ${size} ${size * 0.72}`}
      role="img"
      aria-label={`${v}/100`}
    >
      <title>{`${v}/100`}</title>
      <path
        d={`M ${cx - r},${cy} A ${r},${r} 0 0 1 ${cx + r},${cy}`}
        fill="none"
        stroke="#eef0f4"
        strokeWidth={12}
        strokeLinecap="round"
      />
      <path
        d={`M ${cx - r},${cy} A ${r},${r} 0 0 1 ${arc(v / 100)}`}
        fill="none"
        stroke={color}
        strokeWidth={12}
        strokeLinecap="round"
      />
      <circle cx={x} cy={y} r={6} fill={color} />
      <text
        x={cx}
        y={cy - 6}
        textAnchor="middle"
        className="font-mono"
        fontSize={size * 0.2}
        fontWeight={700}
        fill="#11151c"
      >
        {v}
      </text>
    </svg>
  );
}

export interface BarRow {
  /** Plain-text label — also used as the row key. */
  label: string;
  /** Optional rich label (e.g. a flag + name); falls back to `label` when unset. */
  labelNode?: ReactNode;
  value: number;
  risk?: number;
}

/** Horizontal bars; colour by risk when provided. */
export function BarsH({
  data,
  valueFmt = (v) => String(v),
}: {
  data: BarRow[];
  valueFmt?: (v: number) => string;
}): ReactElement {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex flex-col gap-2.5">
      {data.map((d) => {
        const color =
          d.risk == null
            ? 'var(--accent)'
            : d.risk >= 40
              ? 'var(--fraud-fg)'
              : d.risk >= 20
                ? 'var(--suspect-fg)'
                : 'var(--clean-fg)';
        return (
          <div key={d.label}>
            <div className="mb-1 flex items-center justify-between text-[12.5px]">
              <span className="flex min-w-0 items-center gap-1.5 truncate text-[#3a4151]">
                {d.labelNode ?? d.label}
              </span>
              <span className="font-mono text-text-2">{valueFmt(d.value)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-neutral-bg">
              <div
                className="h-full rounded-full"
                style={{ width: `${(d.value / max) * 100}%`, background: color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
